import React, {
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
} from "react";
import {
  ChevronDownIcon,
  ChevronRightIcon,
  XIcon,
  SearchIcon,
} from "@heroicons/react/outline";

/**
 * CategoryHierarchyDropdown - A dropdown that displays categories with their full hierarchy path
 *
 * Features:
 * - Shows hierarchy like "GOLD > RING" or "GOLD > ... > Wedding Ring" when path is too long
 * - Searchable categories
 * - Supports single selection
 * - Shows full path on hover tooltip
 *
 * @param {Object} props
 * @param {Array} props.categories - Array of category objects with _id, name, parentId
 * @param {string} props.value - Selected category _id
 * @param {Function} props.onChange - Callback when selection changes (receives category _id)
 * @param {string} props.placeholder - Placeholder text
 * @param {string} props.label - Label for the dropdown
 * @param {boolean} props.disabled - Whether dropdown is disabled
 * @param {boolean} props.required - Whether field is required
 * @param {string} props.error - Error message to display
 * @param {number} props.maxPathLength - Maximum characters before truncating path (default: 40)
 * @param {boolean} props.showFullPathInList - Show full path in dropdown list (default: true)
 * @param {boolean} props.allowClear - Allow clearing selection (default: true)
 */
const CategoryHierarchyDropdown = ({
  categories = [],
  value,
  onChange,
  placeholder = "Select a category",
  label,
  disabled = false,
  required = false,
  error,
  maxPathLength = 40,
  showFullPathInList = true,
  allowClear = true,
  className = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Build a map of category ID to category for quick lookup
  const categoryMap = useMemo(() => {
    const map = new Map();
    categories.forEach((cat) => map.set(cat._id, cat));
    return map;
  }, [categories]);

  // Get the full path chain for a category
  const getCategoryPath = useCallback(
    (categoryId) => {
      const path = [];
      let current = categoryMap.get(categoryId);

      while (current) {
        path.unshift(current.name);
        const parentId = current.parentId?._id || current.parentId;
        current = parentId ? categoryMap.get(parentId) : null;
      }

      return path;
    },
    [categoryMap]
  );

  // Format path with truncation if needed
  const formatPath = useCallback(
    (path, maxLength = maxPathLength) => {
      if (!path || path.length === 0) return "";

      const fullPath = path.join(" > ");

      if (fullPath.length <= maxLength) {
        return fullPath;
      }

      // If path is too long, show first > ... > last
      if (path.length === 1) {
        return path[0].length > maxLength
          ? path[0].substring(0, maxLength - 3) + "..."
          : path[0];
      }

      if (path.length === 2) {
        const shortPath = `${path[0]} > ${path[1]}`;
        if (shortPath.length <= maxLength) return shortPath;
        // Truncate the last item
        const availableSpace = maxLength - path[0].length - 7; // " > ..."
        return `${path[0]} > ${path[1].substring(0, availableSpace)}...`;
      }

      // For 3+ levels, show first > ... > last
      const first = path[0];
      const last = path[path.length - 1];
      const shortPath = `${first} > ... > ${last}`;

      if (shortPath.length <= maxLength) {
        return shortPath;
      }

      // If still too long, truncate the last name
      const availableSpace = maxLength - first.length - 10; // " > ... > "
      if (availableSpace > 3) {
        return `${first} > ... > ${last.substring(0, availableSpace)}...`;
      }

      // Last resort: just show truncated last item
      return last.length > maxLength
        ? last.substring(0, maxLength - 3) + "..."
        : last;
    },
    [maxPathLength]
  );

  // Get selected category info
  const selectedCategory = useMemo(() => {
    if (!value) return null;
    const category = categoryMap.get(value);
    if (!category) return null;

    const path = getCategoryPath(value);
    return {
      ...category,
      path,
      displayPath: formatPath(path),
      fullPath: path.join(" > "),
    };
  }, [value, categoryMap, getCategoryPath, formatPath]);

  // Build hierarchical list with paths for dropdown
  const categoriesWithPaths = useMemo(() => {
    return categories.map((cat) => {
      const path = getCategoryPath(cat._id);
      const level = path.length - 1;
      return {
        ...cat,
        path,
        displayPath: formatPath(path, 60), // Slightly longer for list
        fullPath: path.join(" > "),
        level,
        indent: level * 16, // Indentation in pixels
      };
    });
  }, [categories, getCategoryPath, formatPath]);

  // Filter categories based on search
  const filteredCategories = useMemo(() => {
    if (!searchTerm) return categoriesWithPaths;

    const searchLower = searchTerm.toLowerCase();
    return categoriesWithPaths.filter(
      (cat) =>
        cat.name.toLowerCase().includes(searchLower) ||
        cat.fullPath.toLowerCase().includes(searchLower) ||
        cat.categoryId?.toLowerCase().includes(searchLower)
    );
  }, [categoriesWithPaths, searchTerm]);

  // Sort by path for better display
  const sortedCategories = useMemo(() => {
    return [...filteredCategories].sort((a, b) => {
      return a.fullPath.localeCompare(b.fullPath);
    });
  }, [filteredCategories]);

  // Handle selection
  const handleSelect = useCallback(
    (categoryId) => {
      onChange?.(categoryId);
      setIsOpen(false);
      setSearchTerm("");
    },
    [onChange]
  );

  // Handle clear
  const handleClear = useCallback(
    (e) => {
      e.stopPropagation();
      onChange?.(null);
    },
    [onChange]
  );

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e) => {
    if (e.key === "Escape") {
      setIsOpen(false);
      setSearchTerm("");
    }
  }, []);

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Label */}
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      {/* Selected Value Display / Trigger */}
      <div
        className={`
          relative flex items-center w-full px-3 py-2 border rounded-lg cursor-pointer
          transition-all duration-200
          ${
            disabled
              ? "bg-gray-100 cursor-not-allowed"
              : "bg-white hover:border-gray-400"
          }
          ${
            error
              ? "border-red-500"
              : isOpen
              ? "border-primary ring-2 ring-primary/20"
              : "border-gray-300"
          }
        `}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        tabIndex={disabled ? -1 : 0}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        {selectedCategory ? (
          <div className="flex-1 flex items-center gap-2 min-w-0">
            <span
              className="truncate text-gray-800"
              title={selectedCategory.fullPath}
            >
              {selectedCategory.displayPath}
            </span>
          </div>
        ) : (
          <span className="flex-1 text-gray-400">{placeholder}</span>
        )}

        <div className="flex items-center gap-1 ml-2">
          {allowClear && selectedCategory && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="p-1 hover:bg-gray-100 rounded-full transition-colors"
              title="Clear selection"
            >
              <XIcon className="w-4 h-4 text-gray-400 hover:text-gray-600" />
            </button>
          )}
          <ChevronDownIcon
            className={`w-4 h-4 text-gray-400 transition-transform ${
              isOpen ? "rotate-180" : ""
            }`}
          />
        </div>
      </div>

      {/* Error Message */}
      {error && <p className="mt-1 text-sm text-red-500">{error}</p>}

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
          {/* Search Input */}
          <div className="p-2 border-b">
            <div className="relative">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                ref={inputRef}
                type="text"
                placeholder="Search categories..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                autoFocus
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>

          {/* Options List */}
          <div className="max-h-64 overflow-y-auto">
            {sortedCategories.length > 0 ? (
              sortedCategories.map((category) => {
                const isSelected = value === category._id;
                const isActive = category.display?.isActive !== false;

                return (
                  <div
                    key={category._id}
                    className={`
                      px-3 py-2 cursor-pointer transition-colors
                      ${
                        isSelected
                          ? "bg-primary/10 text-primary"
                          : "hover:bg-gray-50"
                      }
                      ${!isActive ? "opacity-50" : ""}
                    `}
                    onClick={() => handleSelect(category._id)}
                    role="option"
                    aria-selected={isSelected}
                    title={category.fullPath}
                  >
                    {showFullPathInList ? (
                      <div className="flex items-center gap-2">
                        <ChevronRightIcon className="w-3 h-3 text-gray-400 flex-shrink-0" />
                        <span className="text-sm truncate">
                          {category.displayPath}
                        </span>
                      </div>
                    ) : (
                      <div
                        className="flex items-center"
                        style={{ paddingLeft: `${category.indent}px` }}
                      >
                        <span className="text-sm">{category.name}</span>
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="px-3 py-4 text-center text-sm text-gray-500">
                {searchTerm ? "No categories found" : "No categories available"}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Helper function to format category path - can be used standalone
 * @param {Array} categories - All categories
 * @param {string} categoryId - The category ID to get path for
 * @param {number} maxLength - Maximum path length before truncating
 * @returns {Object} { path: string[], displayPath: string, fullPath: string }
 */
export const getCategoryHierarchyPath = (
  categories,
  categoryId,
  maxLength = 40
) => {
  const categoryMap = new Map();
  categories.forEach((cat) => categoryMap.set(cat._id, cat));

  const path = [];
  let current = categoryMap.get(categoryId);

  while (current) {
    path.unshift(current.name);
    const parentId = current.parentId?._id || current.parentId;
    current = parentId ? categoryMap.get(parentId) : null;
  }

  const fullPath = path.join(" > ");

  if (fullPath.length <= maxLength) {
    return { path, displayPath: fullPath, fullPath };
  }

  // Truncation logic
  if (path.length === 1) {
    const displayPath =
      path[0].length > maxLength
        ? path[0].substring(0, maxLength - 3) + "..."
        : path[0];
    return { path, displayPath, fullPath };
  }

  if (path.length === 2) {
    const shortPath = `${path[0]} > ${path[1]}`;
    if (shortPath.length <= maxLength) {
      return { path, displayPath: shortPath, fullPath };
    }
    const availableSpace = maxLength - path[0].length - 7;
    const displayPath = `${path[0]} > ${path[1].substring(
      0,
      availableSpace
    )}...`;
    return { path, displayPath, fullPath };
  }

  // For 3+ levels
  const first = path[0];
  const last = path[path.length - 1];
  let displayPath = `${first} > ... > ${last}`;

  if (displayPath.length > maxLength) {
    const availableSpace = maxLength - first.length - 10;
    if (availableSpace > 3) {
      displayPath = `${first} > ... > ${last.substring(0, availableSpace)}...`;
    } else {
      displayPath =
        last.length > maxLength
          ? last.substring(0, maxLength - 3) + "..."
          : last;
    }
  }

  return { path, displayPath, fullPath };
};

export default CategoryHierarchyDropdown;
